import { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Link } from 'react-router-dom';

interface LinkCardProps {
  label: string;
  sublabel?: string;
  icon: ReactNode;
  /** Controls the icon container's width and height. Defaults to 28. */
  iconSize?: 28 | 32;
  to: string;
  /** Blue primary variant — use for the main call-to-action. */
  primary?: boolean;
}

const useStyles = makeStyles({
  card: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px',
    borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.12)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background 120ms',
  },
  primaryCard: {
    backgroundColor: '#1F5493',
    // JSS $ref nesting generates ".primaryCard .label { color: #fff !important }"
    // — two-class specificity (0,2,0) with !important beats any global
    // single-class or element-level baseline rule from MUI / Backstage.
    '& $label': { color: '#ffffff !important' },
    '& $sublabel': { color: '#ffffff !important' },
    '& $chevron': { color: '#ffffff' },
    '&:hover': {
      backgroundColor: '#163a66',
    },
  },
  secondaryCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    color: 'var(--arla-text-primary-color)',
    '&:hover': {
      backgroundColor: 'var(--arla-link-card-hover-bg)',
    },
  },
  iconContainer: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  meta: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  label: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: 1.4,
    margin: 0,
    whiteSpace: 'nowrap',
  },
  sublabel: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.4,
    margin: 0,
    whiteSpace: 'nowrap',
  },
  secondarySublabel: {
    color: '#757575',
  },
  chevron: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    color: '#767676',
    '& svg': {
      width: '20px',
      height: '20px',
    },
    '[data-theme-mode="dark"] &': {
      color: '#ffffff',
    },
  },
});

export const LinkCard = ({
  label,
  sublabel,
  icon,
  iconSize = 28,
  to,
  primary = false,
}: LinkCardProps) => {
  const classes = useStyles();

  return (
    <Link
      to={to}
      className={`${classes.card} ${primary ? classes.primaryCard : classes.secondaryCard}`}
    >
      <div className={classes.iconContainer} style={{ width: iconSize, height: iconSize }}>
        {icon}
      </div>
      <div className={classes.meta}>
        <p className={classes.label}>{label}</p>
        {sublabel && (
          <p className={`${classes.sublabel}${!primary ? ` ${classes.secondarySublabel}` : ''}`}>
            {sublabel}
          </p>
        )}
      </div>
      <span className={classes.chevron}>
        <ChevronRightIcon />
      </span>
    </Link>
  );
};
